import React, { useCallback, useContext, useRef, useState } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { Identifier } from 'dnd-core';
import { ReactComponent as DragIcon } from 'assets/icons/drag-icon.svg';
import update from 'immutability-helper';
import cn from 'clsx';
import styles from './DraggableList.module.scss';

interface DragItem {
  id: string | number;
  index: number;
  type: string;
};

type DropIndex = {
  index: number | null;
  position: 'before' | 'after' | null;
}

type DndListContext<T extends Pick<DragItem, 'id'> = Pick<DragItem, 'id'>> = {
  items: T[];
  selectedItems: T['id'][];
  onSelectItem: (id: T['id']) => void;
  onSort: (dragIndex: number, hoverIndex: number) => void;
  isDragRestricted: boolean;
  setDropIndex: ({ index, position }: DropIndex) => void;
  dropIndex: DropIndex;
};

const DndListContext = React.createContext({} as DndListContext);

export function DraggableItem<T extends Pick<DragItem, 'id'> = Pick<DragItem, 'id'>>({
  id,
  index,
  children,
  className,
} : {
  id: T['id'];
  index: number;
  children: React.ReactNode;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);

  const {
    selectedItems,
    onSelectItem,
    onSort,
    isDragRestricted,
    dropIndex,
    setDropIndex
  } = useContext(DndListContext);

  const handleSelect = () => {
    if (!onSelectItem) return;

    onSelectItem(id);
  };

  const [{ handlerId, isOver }, drop] = useDrop<
    DragItem,
    void,
    { handlerId: Identifier | null, isOver: boolean }
  >({
    accept: 'item',
    options: () => ({
      anchorX: 0.8,
    }), 
    collect: (monitor) => ({
      handlerId: monitor.getHandlerId(),
      isOver: monitor.isOver({ shallow: true })
    }),
    canDrop: () => !isDragRestricted,
    hover: (item, monitor) => {
      const dragIndex = item.index;
      const hoverIndex = index;

      if (dragIndex === hoverIndex) {
        return;
      }

      let position: DropIndex['position']
        = dragIndex < hoverIndex ? 'after' : 'before';
      
      setDropIndex && setDropIndex({ index: hoverIndex, position: position });
    },
    drop: (item) => {

      if (!ref.current) {
        return
      }

      const dragIndex = item.index;
      const hoverIndex = index;

      if (dragIndex === hoverIndex) {
        setDropIndex && setDropIndex({ index: null, position: null });
        return;
      }

      if (onSort) { 
        onSort(dragIndex, hoverIndex);
      }

      item.index = hoverIndex;
      setDropIndex && setDropIndex({ index: null, position: null });
    },
  })

  const [{ isDragging }, drag] = useDrag({
    type: 'item',
    item: () => {
      return { id, index }
    },
    collect: (monitor: any) => ({
      isDragging: monitor.isDragging(),
    }),
  })

  if (!isDragRestricted) {
    drag(drop(ref));
  }

  return (
    <div
      onClick={handleSelect}
      className={cn(
        'dnd-item',
        styles.dndItem,
        selectedItems.includes(id) && styles.dndItemSelected,
        isOver && styles.beDragged,
        (isOver && dropIndex.position === 'before') && styles.beDraggedBefore,
        (isOver && dropIndex.position === 'after') && styles.beDraggedAfter,
        isDragging && styles.isDragging,
        className
      )}
      ref={ref}
      data-handler-id={handlerId}
    >
      <div className={styles.dragIcon}>
        <DragIcon />
      </div>
      <div className={styles.dndItemContent}>{children}</div>
    </div>
  );
};

type Props<T extends Pick<DragItem, 'id'> = Pick<DragItem, 'id'>> = {
  items: T[];
  selectedItems: T['id'][];
  children: React.ReactNode;
  onSort?: (items: T[]) => void;
  onSelect?: (id: T['id']) => void;
  isDragRestricted?: boolean;
}

function DraggableList<T extends Pick<DragItem, 'id'> = Pick<DragItem, 'id'>>({
  items,
  selectedItems = [],
  onSort,
  onSelect,
  isDragRestricted = false,
  children
}: Props<T>) {

  const [dropIndex, setDropIndex] = useState<DropIndex>({ index: null, position: null });
  
  const sortItem = useCallback((dragIndex: number, hoverIndex: number) => {
    const sortedItems = update(items, {
      $splice: [
        [dragIndex, 1],
        [hoverIndex, 0, items[dragIndex] as T],
      ],
    });


    if (onSort) {
      onSort(sortedItems);
    }
  }, [items, onSort]);

  const onSelectItem = (v: T['id']) => {
    if (onSelect) {
      onSelect(v)
    };
  };

  return (
    <DndListContext.Provider value={{
      items,
      selectedItems,
      onSelectItem,
      onSort: sortItem,
      isDragRestricted,
      dropIndex,
      setDropIndex
    }}>
      <div className={cn('dnd-wrapper', styles.dndWrapper)}>
        <div className={cn('dnd-items-list', styles.dndItemsList)}>
          {(!items.length || !items || !React.Children.count(children)) ? (
            <div className={cn('dnd-item', styles.dndItem, styles.dndItemEmpty)}>
              <div className={styles.dndItemContent} />
            </div>
          ) : (
            <>{children}</>
          )}

        </div>
      </div>
    </DndListContext.Provider>
  );
}

DraggableList.Item = DraggableItem;

export { DraggableList};